<template>
  <modal
    :show="isVisible"
    :title="modalTitle"
    elem-id="modal_acoes_marketing"
    size="large"
    :close-label="t('Cancelar')"
    :save-label="isSaving ? t('Salvando') : t('Salvar')"
    :save-fn="handleSave"
    @close="handleClose"
    @dismiss="handleClose"
  >
    <template v-if="isVisible">
      <acoes-marketing-form
        ref="formRef"
        :acaoId="currentAcaoId"
        :isEditMode="isEditMode"
        @submit-success="handleFormSuccess"
        @submit-error="handleFormError"
        @validation-failed="handleValidationFailed"
      />
    </template>
  </modal>
</template>

<script setup lang="ts">
import { ref, computed, nextTick, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { eventBus } from '../../eventBus';
import AcoesMarketingForm from './AcoesMarketingForm.vue';
import { log, warn as logWarn, error as logError } from '../../utils/DebugLogger';

type AcaoId = number | string;

interface AcoesMarketingFormInstance {
  resetForm: () => void;
  loadForEdit: () => Promise<void>;
  handleSubmit: () => Promise<void>;
  validateForm: () => boolean;
}

interface ModalOpenPayload {
  id?: AcaoId;
  action?: 'create' | 'edit';
}

const { t } = useI18n();

const isVisible = ref<boolean>(false);
const isEditMode = ref<boolean>(false);
const currentAcaoId = ref<AcaoId | null>(null);
const formRef = ref<AcoesMarketingFormInstance | null>(null);
const isSaving = ref<boolean>(false);

const modalTitle = computed<string>(() => isEditMode.value ? t('Editar Ação de Marketing') : t('Nova Ação de Marketing'));

const openForCreate = (): void => {
  log('AcoesMarketingModal', 'openForCreate', 'Opening modal for create');
  isVisible.value = true;
  isEditMode.value = false;
  currentAcaoId.value = null;
  nextTick(() => {
    formRef.value?.resetForm();
  });
};

const openForEdit = async (acaoId: AcaoId): Promise<void> => {
  log('AcoesMarketingModal', 'openForEdit', 'Opening modal for edit', acaoId);
  isVisible.value = true;
  isEditMode.value = true;
  currentAcaoId.value = acaoId;
  await nextTick();
  if (formRef.value) {
    await formRef.value.loadForEdit();
  }
};

const handleClose = (): void => {
  log('AcoesMarketingModal', 'handleClose', 'Closing modal');
  isVisible.value = false;
  isEditMode.value = false;
  currentAcaoId.value = null;
  isSaving.value = false;
  formRef.value?.resetForm();
};

const handleSave = async (): Promise<void> => {
  if (formRef.value) {
    isSaving.value = true;
    try {
      await formRef.value.handleSubmit();
    } finally {
      isSaving.value = false;
    }
  }
};

const handleFormSuccess = (): void => {
  log('AcoesMarketingModal', 'handleFormSuccess', 'Form submitted successfully');
  eventBus.emit('acoes-marketing.saved');
  handleClose();
};

const handleFormError = (error: unknown): void => {
  logError('AcoesMarketingModal', 'handleFormError', 'Form submission error', error);
};

const handleValidationFailed = (): void => {
  logWarn('AcoesMarketingModal', 'handleValidationFailed', 'Client-side validation failed');
};

const handleModalOpen = (data?: ModalOpenPayload): void => {
  log('AcoesMarketingModal', 'handleModalOpen', 'Modal open event received', data);
  if (data?.id) {
    void openForEdit(data.id);
  } else {
    openForCreate();
  }
};

onMounted(() => {
  eventBus.on('modal.acoes-marketing.open', handleModalOpen);
  eventBus.on('modal.acoes-marketing.close', handleClose);
});

onBeforeUnmount(() => {
  eventBus.off('modal.acoes-marketing.open', handleModalOpen);
  eventBus.off('modal.acoes-marketing.close', handleClose);
});

defineExpose({
  openForCreate,
  openForEdit
});
</script>

<style scoped>
</style>
